/**
 * MyAssignmentsWidget
 * Dashboard-widget som visar inloggad användares öppna tilldelningar.
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import { ClipboardDocumentListIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { BaseDashboardWidget } from './BaseDashboardWidget';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';

interface MyAssignmentItem {
  id: number;
  title: string;
  category_name: string;
  category_color: string | null;
  registration_number: string | null;
  status: string;
  priority: string;
  due_date: string | null;
  assigned_at: string;
}

const PRIORITY_INDICATOR: Record<string, string> = {
  URGENT: 'bg-red-500',
  HIGH: 'bg-orange-500',
  NORMAL: 'bg-blue-500',
  LOW: 'bg-gray-400',
};

const STATUS_COLORS: Record<string, string> = {
  IN_PROGRESS: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  PENDING: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
};

const MyAssignmentsWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <BaseDashboardWidget<MyAssignmentItem>
      icon={ClipboardDocumentListIcon}
      colorScheme="purple"
      title={t('assignments.myAssignments')}
      subtitle={t('assignments.myAssignments')}
      apiEndpoint="/api/dashboard/data/my-assignments"
      emptyMessage={t('assignments.noOpenAssignments')}
      warningBadge={(count) => count > 0 ? `${count} ${t('assignments.open')}` : ''}
      getItemKey={(item) => item.id}
      renderItem={(item) => (
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_INDICATOR[item.priority] || PRIORITY_INDICATOR.NORMAL}`} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{item.title}</div>
            <div className="text-xs text-textSecondary flex gap-2">
              <span>{item.category_name}</span>
              {item.registration_number && <span>• {item.registration_number}</span>}
            </div>
          </div>
          <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[item.status] || STATUS_COLORS.PENDING}`}>
            {item.status === 'IN_PROGRESS' ? t('assignments.statusInProgress') : t('assignments.statusPending')}
          </span>
        </div>
      )}
      footerAction={
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`${ROUTES.DEQUE_ASSIGNMENTS}?scope=mine`); }}
          className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline mt-2"
        >
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
          {t('assignments.viewAll')}
        </button>
      }
    />
  );
};

export default MyAssignmentsWidget;
